import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  TextField,
  Chip,
  IconButton,
  Tooltip,
  alpha,
} from '@mui/material';
import TerminalIcon from '@mui/icons-material/Terminal';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { getEffectiveCaseData, applyParsedField } from '../common/functionMode';
import TypedTestCaseField from './TypedTestCaseField';

const ACCENT = '#137274';

const monoSx = {
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
  fontSize: '0.8rem',
  lineHeight: 1.5,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
};

const chipSx = ({ active, isDark, border }) => ({
  fontWeight: 700,
  bgcolor: active
    ? alpha(ACCENT, isDark ? 0.25 : 0.14)
    : isDark
      ? alpha('#fff', 0.06)
      : alpha(ACCENT, 0.06),
  color: active ? ACCENT : undefined,
  border: active ? `1px solid ${alpha(ACCENT, 0.45)}` : `1px solid ${border}`,
});

/**
 * LeetCode-style console: Test Cases / Test Result tabs.
 */
const IdeConsolePanel = ({
  customInput,
  onCustomInputChange,
  output,
  runResults = null,
  error,
  isCompiling = false,
  isSubmitting = false,
  collapsed = false,
  onToggleCollapse,
  isFullscreen = false,
  height,
  minHeight,
  compact = false,
  dataTour,
  border,
  muted,
  surface,
  isDark,
  idleMessage = 'Run your code to see results here.',
  inputPlaceholder = 'Enter test case input…',
  sampleCases = [],
  problem = null,
  selectedCaseIndex = 0,
  onSelectCase,
  functionMode = false,
  caseOverride = null,
  onCaseOverrideChange,
  useCustomInput: useCustomInputProp,
  onUseCustomInputChange,
  customCases = [],
  selectedCustomIndex = 0,
  onSelectCustomCase,
  onAddCustomCase,
  onRemoveCustomCase,
  onCustomCaseChange,
}) => {
  const [activeTab, setActiveTab] = useState('cases');
  const [useCustomInputInternal, setUseCustomInputInternal] = useState(false);
  const [copied, setCopied] = useState(false);
  const [selectedResultIndex, setSelectedResultIndex] = useState(0);

  const useCustomInput =
    typeof useCustomInputProp === 'boolean' ? useCustomInputProp : useCustomInputInternal;
  const setUseCustomInput = (next) => {
    if (typeof onUseCustomInputChange === 'function') {
      onUseCustomInputChange(next);
    } else {
      setUseCustomInputInternal(next);
    }
  };

  const hasSampleCases = sampleCases.length > 0;
  const hasCustomCases = customCases.length > 0;
  const selectedCase = hasSampleCases ? sampleCases[selectedCaseIndex] : null;
  const selectedCustom = hasCustomCases ? customCases[selectedCustomIndex] : null;
  const signature = problem?.signature;
  const params = signature?.params || [];

  const activeOverride = useCustomInput
    ? { args: selectedCustom?.args || {} }
    : caseOverride;
  const { args: caseArgs } = getEffectiveCaseData(
    useCustomInput ? { args: {} } : selectedCase,
    activeOverride,
  );

  const updateArgField = (name, type, parsed) => {
    if (useCustomInput) {
      onCustomCaseChange?.(selectedCustomIndex, (prev) => ({
        ...prev,
        args: applyParsedField(prev?.args || {}, name, type, parsed),
      }));
      return;
    }
    onCaseOverrideChange?.(selectedCaseIndex, (current) => ({
      args: applyParsedField(current.args, name, type, parsed),
      expected: current.expected,
    }));
  };

  const consoleFieldSx = {
    '& .MuiInputBase-root': {
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
      fontSize: compact ? 13 : 12,
      bgcolor: isDark ? '#010409' : '#fff',
    },
    '& .MuiInputLabel-root': {
      fontWeight: 700,
      fontSize: '0.78rem',
    },
    '& .MuiFormHelperText-root': {
      fontFamily: 'inherit',
      fontSize: '0.68rem',
    },
  };

  useEffect(() => {
    if (isCompiling || isSubmitting || output || error || (runResults && runResults.length)) {
      setActiveTab('result');
    }
  }, [isCompiling, isSubmitting, output, error, runResults]);

  useEffect(() => {
    if (!runResults?.length) {
      setSelectedResultIndex(0);
      return;
    }
    const firstFail = runResults.findIndex((r) => r.status !== 'Accepted');
    setSelectedResultIndex(firstFail >= 0 ? firstFail : 0);
  }, [runResults]);

  useEffect(() => {
    if (hasSampleCases) {
      setUseCustomInput(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset custom mode when problem changes
  }, [problem?._id, problem?.slug, hasSampleCases]);

  const tabs = [
    { id: 'cases', label: compact ? 'Input' : 'Test Cases' },
    { id: 'result', label: compact ? 'Result' : 'Test Result' },
  ];

  const statusLabel = error
    ? 'Error'
    : output
      ? 'Done'
      : isCompiling || isSubmitting
        ? 'Running'
        : 'Idle';
  const statusColor = error
    ? '#cf222e'
    : output
      ? ACCENT
      : isCompiling || isSubmitting
        ? '#d97706'
        : muted;

  const contentMin = compact ? 160 : isFullscreen ? 120 : 140;

  const resultText =
    output ||
    (isCompiling ? 'Executing…' : isSubmitting ? 'Submitting…' : idleMessage);

  const handleCopyResult = async () => {
    let text = '';
    if (runResults?.length) {
      const selected = runResults[selectedResultIndex] || runResults[0];
      text = selected
        ? `${selected.label}: ${selected.status}\n${selected.message || ''}`.trim()
        : '';
    } else {
      text = String(output || '').trim();
    }
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      /* ignore */
    }
  };

  const statusPassed = (status) => status === 'Accepted';
  const selectedResult =
    runResults?.length > 0
      ? runResults[Math.min(selectedResultIndex, runResults.length - 1)]
      : null;
  const selectedResultOk = selectedResult
    ? statusPassed(selectedResult.status)
    : !error;

  const caseButtons = useMemo(
    () =>
      sampleCases.map((_, index) => ({
        id: index,
        label: `Case ${index + 1}`,
      })),
    [sampleCases],
  );

  const showCaseChips = hasSampleCases || hasCustomCases;
  const showFunctionFields =
    functionMode &&
    params.length > 0 &&
    (useCustomInput ? hasCustomCases : selectedCase || !hasSampleCases);
  const showIoSampleField = !functionMode && !useCustomInput && selectedCase;
  const showIoCustomField =
    !functionMode && (useCustomInput ? hasCustomCases : !hasSampleCases);

  const ioFieldValue = useCustomInput
    ? selectedCustom?.input ?? ''
    : customInput;
  const onIoFieldChange = (value) => {
    if (useCustomInput) {
      onCustomCaseChange?.(selectedCustomIndex, (prev) => ({
        ...prev,
        input: value,
      }));
    } else {
      onCustomInputChange?.(value);
    }
  };

  return (
    <Box
      data-tour={dataTour}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: collapsed ? 'auto' : height ?? '100%',
        minHeight: collapsed ? 0 : minHeight ?? 0,
        bgcolor: surface,
        flexShrink: 0,
        overflow: 'hidden',
        borderTop: compact ? `1px solid ${border}` : undefined,
        boxShadow: compact
          ? isDark
            ? '0 -8px 24px rgba(0,0,0,0.35)'
            : '0 -8px 24px rgba(15,60,70,0.1)'
          : undefined,
      }}
    >
      <Box
        sx={{
          px: 1.25,
          borderBottom: collapsed ? 'none' : `1px solid ${border}`,
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          flexShrink: 0,
          minHeight: compact ? 40 : 40,
          bgcolor: compact
            ? isDark
              ? alpha('#fff', 0.03)
              : alpha(ACCENT, 0.04)
            : undefined,
        }}
      >
        {!compact && (
          <TerminalIcon sx={{ color: ACCENT, fontSize: 16, mr: 0.5, opacity: 0.9 }} />
        )}
        {tabs.map((tab) => {
          const selected = activeTab === tab.id;
          return (
            <Box
              key={tab.id}
              component="button"
              type="button"
              onClick={() => {
                if (collapsed) onToggleCollapse?.();
                setActiveTab(tab.id);
              }}
              sx={{
                appearance: 'none',
                border: 0,
                background: 'transparent',
                cursor: 'pointer',
                px: compact ? 1.15 : 1.25,
                py: compact ? 1 : 1.1,
                fontSize: '0.8rem',
                fontWeight: selected ? 800 : 600,
                letterSpacing: '0.02em',
                color: selected ? (isDark ? '#e6edf3' : '#0f2a2b') : muted,
                borderBottom: `2px solid ${selected ? ACCENT : 'transparent'}`,
                mb: '-1px',
                transition: 'color 0.15s, border-color 0.15s',
                '&:hover': {
                  color: isDark ? '#e6edf3' : '#0f2a2b',
                },
              }}
            >
              {tab.label}
            </Box>
          );
        })}
        <Chip
          size="small"
          label={statusLabel}
          sx={{
            height: compact ? 22 : 20,
            fontWeight: 700,
            fontSize: compact ? '0.7rem' : '0.65rem',
            ml: 'auto',
            bgcolor: alpha(statusColor, 0.15),
            color: statusColor,
          }}
        />
        {onToggleCollapse && (
          <Tooltip title={collapsed ? 'Expand panel' : 'Minimize panel'}>
            <IconButton
              size="small"
              onClick={onToggleCollapse}
              aria-label={collapsed ? 'Expand test panel' : 'Minimize test panel'}
              sx={{ color: muted, ml: 0.25 }}
            >
              {collapsed ? (
                <KeyboardArrowUpIcon sx={{ fontSize: 18 }} />
              ) : (
                <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
              )}
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {!collapsed && (
      <Box
        sx={{
          p: compact ? 1.25 : 1.5,
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {activeTab === 'cases' ? (
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {showCaseChips && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
                {caseButtons.map((item) => (
                  <Chip
                    key={item.id}
                    label={item.label}
                    size="small"
                    clickable
                    onClick={() => {
                      setUseCustomInput(false);
                      onSelectCase?.(item.id);
                    }}
                    sx={{
                      ...chipSx({
                        active: !useCustomInput && selectedCaseIndex === item.id,
                        isDark,
                        border,
                      }),
                      color:
                        !useCustomInput && selectedCaseIndex === item.id ? ACCENT : muted,
                    }}
                  />
                ))}

                {customCases.map((item, index) => {
                  const active = useCustomInput && selectedCustomIndex === index;
                  return (
                    <Chip
                      key={item.id || index}
                      label={`Custom ${index + 1}`}
                      size="small"
                      clickable
                      onClick={() => {
                        setUseCustomInput(true);
                        onSelectCustomCase?.(index);
                      }}
                      onDelete={
                        onRemoveCustomCase
                          ? (event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              onRemoveCustomCase(index);
                            }
                          : undefined
                      }
                      deleteIcon={
                        <CloseIcon
                          sx={{ fontSize: '14px !important' }}
                          onMouseDown={(e) => e.stopPropagation()}
                        />
                      }
                      sx={{
                        ...chipSx({ active, isDark, border }),
                        color: active ? ACCENT : muted,
                        '& .MuiChip-deleteIcon': {
                          color: active ? alpha(ACCENT, 0.85) : muted,
                          '&:hover': { color: '#cf222e' },
                        },
                      }}
                    />
                  );
                })}

                <Tooltip title="Add custom test case">
                  <Chip
                    icon={<AddIcon sx={{ fontSize: '16px !important' }} />}
                    label={hasCustomCases ? 'Add' : 'Custom'}
                    size="small"
                    clickable
                    onClick={() => onAddCustomCase?.()}
                    sx={{
                      fontWeight: 700,
                      bgcolor: isDark ? alpha('#fff', 0.06) : alpha(ACCENT, 0.06),
                      color: muted,
                      border: `1px dashed ${border}`,
                      '& .MuiChip-icon': { color: muted, ml: 0.5 },
                    }}
                  />
                </Tooltip>
              </Box>
            )}

            {!showCaseChips && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                <Tooltip title="Add custom test case">
                  <Chip
                    icon={<AddIcon sx={{ fontSize: '16px !important' }} />}
                    label="Custom"
                    size="small"
                    clickable
                    onClick={() => onAddCustomCase?.()}
                    sx={{
                      fontWeight: 700,
                      bgcolor: isDark ? alpha('#fff', 0.06) : alpha(ACCENT, 0.06),
                      color: muted,
                      border: `1px dashed ${border}`,
                      '& .MuiChip-icon': { color: muted, ml: 0.5 },
                    }}
                  />
                </Tooltip>
              </Box>
            )}

            {showFunctionFields && (
              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  overflow: 'auto',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  pt: showCaseChips ? 0.75 : 0,
                }}
              >
                {params.map((param) => (
                  <TypedTestCaseField
                    key={`${useCustomInput ? `custom-${selectedCustomIndex}` : selectedCaseIndex}-${param.name}`}
                    fieldKey={`${useCustomInput ? `custom-${selectedCustomIndex}` : selectedCaseIndex}-${param.name}-arg`}
                    type={param.type}
                    label={`${param.name} (${param.type})`}
                    value={caseArgs?.[param.name]}
                    onChange={(parsed) => updateArgField(param.name, param.type, parsed)}
                    sx={consoleFieldSx}
                  />
                ))}
              </Box>
            )}

            {(showIoSampleField || showIoCustomField) && (
              <TextField
                label={useCustomInput ? `Custom ${selectedCustomIndex + 1} input` : 'Input'}
                placeholder={useCustomInput ? inputPlaceholder : 'Edit sample input…'}
                multiline
                fullWidth
                value={ioFieldValue}
                onChange={(e) => onIoFieldChange(e.target.value)}
                sx={{
                  flex: 1,
                  minHeight: 0,
                  display: 'flex',
                  '& .MuiInputBase-root': {
                    height: '100%',
                    alignItems: 'stretch',
                    fontFamily:
                      'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                    fontSize: compact ? 14 : 13,
                    bgcolor: isDark ? '#0d1117' : '#f6f8fa',
                  },
                  '& .MuiInputBase-input': {
                    height: '100% !important',
                    overflow: 'auto !important',
                    boxSizing: 'border-box',
                  },
                  '& .MuiInputLabel-root': {
                    fontWeight: 700,
                    fontSize: '0.78rem',
                  },
                }}
                InputProps={{
                  sx: { minHeight: contentMin },
                }}
              />
            )}
          </Box>
        ) : (
          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
            {runResults && runResults.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, alignItems: 'center' }}>
                {runResults.map((result, index) => {
                  const ok = statusPassed(result.status);
                  const active = selectedResultIndex === index;
                  return (
                    <Chip
                      key={result.key || result.label}
                      label={result.label}
                      size="small"
                      clickable
                      onClick={() => setSelectedResultIndex(index)}
                      sx={{
                        ...chipSx({ active, isDark, border }),
                        color: active ? (ok ? ACCENT : '#cf222e') : muted,
                        border: active
                          ? `1px solid ${ok ? alpha(ACCENT, 0.55) : alpha('#cf222e', 0.55)}`
                          : `1px solid ${border}`,
                        bgcolor: active
                          ? ok
                            ? alpha(ACCENT, isDark ? 0.25 : 0.14)
                            : alpha('#cf222e', isDark ? 0.25 : 0.12)
                          : isDark
                            ? alpha('#fff', 0.06)
                            : alpha(ACCENT, 0.06),
                      }}
                    />
                  );
                })}
              </Box>
            )}

            <Box
              sx={{
                position: 'relative',
                flex: 1,
                minHeight: contentMin,
                borderRadius: 1.5,
                border: `1px solid ${border}`,
                bgcolor: selectedResult
                  ? selectedResultOk
                    ? isDark
                      ? alpha(ACCENT, 0.1)
                      : '#f0faf4'
                    : isDark
                      ? '#3d1214'
                      : '#fdecea'
                  : error
                    ? isDark
                      ? '#3d1214'
                      : '#fdecea'
                    : isDark
                      ? '#0d1117'
                      : '#f6f8fa',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              }}
            >
              <Tooltip
                title={copied ? 'Copied!' : 'Copy output'}
                placement="top-end"
                arrow
                slotProps={{
                  popper: {
                    modifiers: [{ name: 'offset', options: { offset: [0, -4] } }],
                  },
                }}
              >
                <Box
                  component="span"
                  sx={{
                    position: 'absolute',
                    top: 6,
                    right: 6,
                    zIndex: 1,
                    display: 'inline-flex',
                    lineHeight: 0,
                  }}
                >
                  <IconButton
                    size="small"
                    onClick={handleCopyResult}
                    disabled={
                      !(
                        (selectedResult && String(selectedResult.message || '').trim()) ||
                        String(output || '').trim()
                      )
                    }
                    aria-label="Copy test result"
                    sx={{
                      color: muted,
                      bgcolor: alpha(isDark ? '#000' : '#fff', 0.65),
                      '&:hover': {
                        bgcolor: alpha(isDark ? '#000' : '#fff', 0.9),
                      },
                    }}
                  >
                    <ContentCopyIcon sx={{ fontSize: 15 }} />
                  </IconButton>
                </Box>
              </Tooltip>

              <Box
                sx={{
                  height: '100%',
                  p: compact ? 1.5 : 1.25,
                  pr: 4.5,
                  color: selectedResult
                    ? selectedResultOk
                      ? isDark
                        ? '#c9d1d9'
                        : '#0f3d2e'
                      : isDark
                        ? '#ffa198'
                        : '#b42318'
                    : error
                      ? isDark
                        ? '#ffa198'
                        : '#b42318'
                      : isDark
                        ? '#c9d1d9'
                        : '#0f3d2e',
                  fontFamily:
                    'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                  fontSize: compact ? 14 : 13,
                  whiteSpace: 'pre-wrap',
                  overflow: 'auto',
                  ...monoSx,
                }}
              >
                {selectedResult
                  ? selectedResult.message || '(no output)'
                  : resultText}
              </Box>
            </Box>
          </Box>
        )}
      </Box>
      )}
    </Box>
  );
};

export default IdeConsolePanel;
